import axios from 'axios';

// API Base URL bestimmen
const API_BASE_URL = (() => {
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  // Lokale Entwicklung (localhost) - immer Backend auf Port 5000
  if (isLocalhost) {
    console.log('[API] Local development detected - using localhost:5000');
    return 'http://localhost:5000/api';
  }

  // Explizit gesetzte API URL für spezielle Fälle
  if (process.env.REACT_APP_API_URL && !process.env.REACT_APP_API_URL.includes('localhost')) {
    console.log('[API] Using REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    return process.env.REACT_APP_API_URL;
  }

  // Production/Server - relative URL verwenden (gleiche Domain)
  console.log('[API] Server/Production mode - using relative URL');
  return '/api';
})();

console.log('[API] Final baseURL =', API_BASE_URL);
console.log('[API] NODE_ENV =', process.env.NODE_ENV);
console.log('[API] hostname =', window.location.hostname);

// Axios-Instanz erstellen
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request Interceptor - JWT Token zu Requests hinzufügen
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor - automatisches Logout NUR bei 401 (nicht mehr angemeldet).
// 403 bedeutet "angemeldet, aber nicht berechtigt" (z.B. noch nicht freigeschaltet)
// und wird durchgereicht, damit die Seite die Meldung des Backends anzeigen kann.
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const isAuthRequest = url.includes('/auth/');
    const isOnLoginPage = window.location.pathname.startsWith('/login');

    // Beim Login selbst nicht ausloggen/umleiten - sonst reisst der Reload
    // die Fehlermeldung ("Ungueltige Anmeldedaten") weg, bevor man sie liest.
    if (status === 401 && !isAuthRequest && !isOnLoginPage) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API-Funktionen
export const authAPI = {
  // Registrierung
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Login
  login: async (emailOrUsername, password) => {
    const response = await api.post('/auth/login', { emailOrUsername, password });
    return response.data;
  },

  // Bestaetigungsmail erneut anfordern
  resendVerification: async (emailOrUsername) => {
    const response = await api.post('/auth/resend-verification', { emailOrUsername });
    return response.data;
  },

  // E-Mail-Verifizierung
  verifyEmail: async (token) => {
    const response = await api.get(`/auth/verify-email/${token}`);
    return response.data;
  },

  // Passwort vergessen – Reset-Link anfordern
  forgotPassword: async (email) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  // Passwort zurücksetzen – neues Passwort mit Token setzen
  resetPassword: async (token, password) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

export const userAPI = {
  // Aktueller Benutzer
  getCurrentUser: async () => {
    const response = await api.get('/users/me');
    return response.data;
  },

  // Profil laden (alternative Funktion)
  getProfile: async () => {
    const response = await api.get('/users/me');
    return response.data;
  },

  // Profil aktualisieren
  updateProfile: async (profileData) => {
    const response = await api.put('/users/profile', profileData);
    return response.data;
  },

  // Passwort ändern
  changePassword: async (passwordData) => {
    const response = await api.put('/users/change-password', passwordData);
    return response.data;
  },

  // Alle Benutzer (Admin)
  getAllUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  // Konten, die auf Freischaltung warten (Admin)
  getPendingUsers: async () => {
    const response = await api.get('/users/pending');
    return response.data;
  },

  // Konto sperren oder entsperren (Admin)
  blockUser: async (userId, blocked) => {
    const response = await api.put(`/users/${userId}/block`, { blocked });
    return response.data;
  },

  // Konto freischalten (Admin)
  activateUser: async (userId) => {
    const response = await api.put(`/users/activate/${userId}`);
    return response.data;
  },

  // User erstellen (Admin)
  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  // User aktualisieren (Admin)
  updateUser: async (userId, userData) => {
    const response = await api.put(`/users/${userId}`, userData);
    return response.data;
  },

  // User löschen (Admin)
  deleteUser: async (userId) => {
    const response = await api.delete(`/users/${userId}`);
    return response.data;
  },
  // User zu Administrator befördern (Admin)
  promoteToAdmin: async (userId) => {
    const response = await api.put(`/users/${userId}/promote`, { role: 'admin' });
    return response.data;
  },
};

export const gameAPI = {
  // Alle Spiele
  getAllGames: async (category = null) => {
    const params = category && category !== 'all' ? { category } : {};
    const response = await api.get('/games', { params })
      console.log('API Response:', response.data);;
    return response.data;
  },

  // Einzelnes Spiel
  getGame: async (gameId) => {
    const response = await api.get(`/games/${gameId}`);
    return response.data;
  },

  // Spiel erstellen (Admin)
  createGame: async (gameData) => {
    const response = await api.post('/games', gameData);
    console.log('API Response:', response.data);
    return response.data;
  },

  // Spiel aktualisieren (Admin)
  updateGame: async (gameId, gameData) => {
    const response = await api.put(`/games/${gameId}`, gameData);
    return response.data;
  },

  // Spiel löschen (Admin)
  deleteGame: async (gameId) => {
    const response = await api.delete(`/games/${gameId}`);
    return response.data;
  },
};

export const registrationAPI = {
  // Zu Spiel anmelden (Einzelanmeldung - Legacy)
  registerForGame: async (gameId, ticketType, message = '') => {
    const response = await api.post('/registrations', { gameId, ticketType, message });
    return response.data;
  },

  // Gruppenanmeldung (neue Funktion)
  register: async (registrationData) => {
    const response = await api.post('/registrations', registrationData);
    return response.data;
  },

  // Anmeldung stornieren
  cancelRegistration: async (gameId) => {
    const response = await api.delete(`/registrations/${gameId}`);
    return response.data;
  },

  // Meine Anmeldungen
  getMyRegistrations: async () => {
    const response = await api.get('/registrations/my');
    return response.data;
  },

  // Meine Anmeldungen (alternative Funktion)
  getUserRegistrations: async () => {
    const response = await api.get('/registrations/my');
    return response.data;
  },

  // Anmeldung prüfen
  checkRegistration: async (gameId) => {
    const response = await api.get(`/registrations/check/${gameId}`);
    return response.data;
  },

  // Alle eigenen Registrierungen laden
  getMyRegistrationsList: async () => {
    const response = await api.get('/registrations/my');
    return response.data;
  },

  // Registrierung genehmigen (Admin)
  approveRegistration: async (registrationId) => {
    const response = await api.put(`/registrations/${registrationId}/approve`);
    return response.data;
  },

  // Registrierung ablehnen (Admin)
  rejectRegistration: async (registrationId) => {
    const response = await api.put(`/registrations/${registrationId}/reject`);
    return response.data;
  },

  // Registrierungsstatus aktualisieren (Admin)
  updateRegistrationStatus: async (registrationId, status) => {
    const response = await api.put(`/registrations/${registrationId}/status`, { status });
    return response.data;
  },

  // Registrierung aktualisieren (z.B. Gruppenanmeldung bearbeiten)
  updateRegistration: async (gameId, registrationData) => {
    const response = await api.put(`/registrations/${gameId}`, registrationData);
    return response.data;
  },
};

export const newsAPI = {
  // Alle News
  getAllNews: async () => {
    const response = await api.get('/news');
    return response.data;
  },

  // Einzelne News
  getNews: async (newsId) => {
    const response = await api.get(`/news/${newsId}`);
    return response.data;
  },

  // News erstellen (Admin)
  createNews: async (newsData) => {
    const response = await api.post('/news', newsData);
    return response.data;
  },

  // News aktualisieren (Admin)
  updateNews: async (newsId, newsData) => {
    const response = await api.put(`/news/${newsId}`, newsData);
    return response.data;
  },

  // News löschen (Admin)
  deleteNews: async (newsId) => {
    const response = await api.delete(`/news/${newsId}`);
    return response.data;
  },
};

export const settingsAPI = {
  // Alle Einstellungen laden (Admin)
  getSettings: async () => {
    const response = await api.get('/settings');
    return response.data;
  },

  // Einstellung aktualisieren (Admin)
  updateSetting: async (key, value) => {
    const response = await api.put(`/settings/${key}`, { value });
    return response.data;
  },

  // Einzelne Einstellung laden (Admin)
  getSetting: async (key) => {
    const response = await api.get(`/settings/${key}`);
    return response.data;
  },
};

export const drinksAPI = {
  // Alle Getränke laden (öffentlich)
  getAllDrinks: async () => {
    const response = await api.get('/drinks');
    return response.data;
  },

  // Getränk erstellen (Admin)
  createDrink: async (drinkData) => {
    const response = await api.post('/drinks', drinkData);
    return response.data;
  },

  // Getränk aktualisieren (Admin)
  updateDrink: async (drinkId, drinkData) => {
    const response = await api.put(`/drinks/${drinkId}`, drinkData);
    return response.data;
  },

  // Getränk löschen (Admin)
  deleteDrink: async (drinkId) => {
    const response = await api.delete(`/drinks/${drinkId}`);
    return response.data;
  },
};

export const adminAPI = {
  // Dashboard-Kennzahlen
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },

  // Zahlung markieren
  markPaid: async (registrationId, paid, amount) => {
    const response = await api.put(`/admin/registrations/${registrationId}/paid`, { paid, amount });
    return response.data;
  },

  // Mitglieder für Export
  getMembers: async () => {
    const response = await api.get('/admin/members');
    return response.data;
  },

  // Massen-E-Mail
  sendMassEmail: async (payload) => {
    const response = await api.post('/admin/mass-email', payload);
    return response.data;
  },
};

export const shopAPI = {
  getProducts: async () => (await api.get('/shop/products')).data,
  getAllProducts: async () => (await api.get('/shop/products/all')).data,
  createProduct: async (product) => (await api.post('/shop/products', product)).data,
  updateProduct: async (id, product) => (await api.put(`/shop/products/${id}`, product)).data,
  deleteProduct: async (id) => (await api.delete(`/shop/products/${id}`)).data,

  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post('/shop/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  createOrder: async (items, note) => (await api.post('/shop/orders', { items, note })).data,
  getMyOrders: async () => (await api.get('/shop/orders/my')).data,
  getAllOrders: async () => (await api.get('/shop/orders')).data,
  updateOrder: async (id, changes) => (await api.put(`/shop/orders/${id}`, changes)).data,
  deleteOrder: async (id) => (await api.delete(`/shop/orders/${id}`)).data
};

export default api;
